
import React, { useState, useEffect, useRef } from 'react';
import type { User, Language, WeekStartDay, ThemeType, FontSizeMode, DietTypes } from '../types';
import CogIcon from './icons/CogIcon';
import ShareIcon from './icons/ShareIcon';
import ChevronDownIcon from './icons/ChevronDownIcon';
import InstallAppSetting from './InstallAppSetting';

const MCP_SSE_BASE = 'https://mealpai-mcp-998444740866.europe-west2.run.app/mcp/sse';
const TOOLS_API_PATH = '/api/ai/tools';
import { getTranslation } from '../utils/translations';
import { EMPTY_DIET_TYPES, normalizeDietTypes } from '../utils/dietTypes';
import { normalizeKnownAllergies, type KnownAllergyId } from '../utils/allergies';
import KnownAllergiesSelector from './KnownAllergiesSelector';
import DietTypesSelector from './DietTypesSelector';
import { encryptGeminiKey, revealGeminiKey } from '../services/geminiService';
import { applyFontSize, getFontSizeMode, getFontSizeMultiplier, saveFontSizePreferences } from '../utils/fontSize';

interface SettingsModalProps {
    isOpen: boolean;
    onClose: () => void;
    user: User;
    onSave: (details: Partial<User>) => Promise<void>;
    guidedMode?: boolean;
}

const REVEAL_TIMEOUT_MS = 30_000;

const SettingsModal: React.FC<SettingsModalProps> = ({ isOpen, onClose, user, onSave, guidedMode = false }) => {
    const [language, setLanguage] = useState<Language>('en');
    const [weekStartDay, setWeekStartDay] = useState<WeekStartDay>('Monday');
    const [theme, setTheme] = useState<ThemeType>('light');
    const [fontSizeMode, setFontSizeMode] = useState<FontSizeMode>('system');
    const [fontSizeMultiplier, setFontSizeMultiplier] = useState(100);
    const [userDietTypes, setUserDietTypes] = useState<DietTypes>({ ...EMPTY_DIET_TYPES });
    const [knownAllergies, setKnownAllergies] = useState<KnownAllergyId[]>([]);
    const [intermittentFastingEnabled, setIntermittentFastingEnabled] = useState(false);
    const [allowHouseholdInvitations, setAllowHouseholdInvitations] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState('');

    // Personal Gemini Key state
    const [geminiKeyInput, setGeminiKeyInput] = useState('');
    const [showGeminiInput, setShowGeminiInput] = useState(false);
    const [isSavingGeminiKey, setIsSavingGeminiKey] = useState(false);
    const [isRevealingKey, setIsRevealingKey] = useState(false);
    const [revealedKey, setRevealedKey] = useState('');
    const [geminiKeyError, setGeminiKeyError] = useState('');
    const [isAiSectionExpanded, setIsAiSectionExpanded] = useState(false);
    const [isDietSectionExpanded, setIsDietSectionExpanded] = useState(false);
    const [isAllergiesSectionExpanded, setIsAllergiesSectionExpanded] = useState(false);
    const [showGeminiInfo, setShowGeminiInfo] = useState(false);
    const [isIntegrationsExpanded, setIsIntegrationsExpanded] = useState(false);
    const [mcpUrlCopied, setMcpUrlCopied] = useState(false);
    const [toolsApiUrlCopied, setToolsApiUrlCopied] = useState(false);
    const revealTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    const mcpCopyTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    const toolsApiCopyTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

    const t = (key: any) => getTranslation(language, key);
    const hasGeminiKey = !!(user.encryptedGeminiKey?.trim());
    const selectedDietCount = Object.values(userDietTypes).filter(Boolean).length;
    const selectedAllergyCount = knownAllergies.length;

    useEffect(() => {
        if (!isOpen || !user) return;

        setLanguage(user.language || 'en');
        setWeekStartDay(user.weekStartDay || 'Monday');
        setTheme(user.theme || 'light');
        setUserDietTypes(normalizeDietTypes(user.userDietTypes));
        setKnownAllergies(normalizeKnownAllergies(user.knownAllergies));
        setIntermittentFastingEnabled(user.intermittentFastingEnabled === true);
        setAllowHouseholdInvitations(user.allowHouseholdInvitations === true);
        setFontSizeMode(getFontSizeMode());
        setFontSizeMultiplier(getFontSizeMultiplier());
        setError('');
        setGeminiKeyInput('');
        setShowGeminiInput(false);
        setRevealedKey('');
        setGeminiKeyError('');
        setIsAiSectionExpanded(false);
        setIsDietSectionExpanded(false);
        setIsAllergiesSectionExpanded(false);
        setShowGeminiInfo(false);
        setIsIntegrationsExpanded(false);
        setMcpUrlCopied(false);
        setToolsApiUrlCopied(false);
        return () => {
            if (revealTimerRef.current) clearTimeout(revealTimerRef.current);
            if (mcpCopyTimerRef.current) clearTimeout(mcpCopyTimerRef.current);
            if (toolsApiCopyTimerRef.current) clearTimeout(toolsApiCopyTimerRef.current);
        };
    }, [isOpen, user.uid]);

    const handleCopyMcpUrl = () => {
        if (!user.apiKey?.trim()) return;
        navigator.clipboard.writeText(`${MCP_SSE_BASE}?${user.apiKey}`);
        setMcpUrlCopied(true);
        if (mcpCopyTimerRef.current) clearTimeout(mcpCopyTimerRef.current);
        mcpCopyTimerRef.current = setTimeout(() => setMcpUrlCopied(false), 2500);
    };

    const toolsApiBaseUrl =
        typeof window !== 'undefined'
            ? `${window.location.origin}${TOOLS_API_PATH}`
            : `https://meat-meal-planner.web.app${TOOLS_API_PATH}`;

    const handleCopyToolsApiUrl = () => {
        navigator.clipboard.writeText(toolsApiBaseUrl);
        setToolsApiUrlCopied(true);
        if (toolsApiCopyTimerRef.current) clearTimeout(toolsApiCopyTimerRef.current);
        toolsApiCopyTimerRef.current = setTimeout(() => setToolsApiUrlCopied(false), 2500);
    };

    const handleSaveGeminiKey = async () => {
        const trimmed = geminiKeyInput.trim();
        if (!trimmed) return;
        setIsSavingGeminiKey(true);
        setGeminiKeyError('');
        try {
            const encryptedKey = await encryptGeminiKey(trimmed);
            await onSave({ encryptedGeminiKey: encryptedKey });
            setGeminiKeyInput('');
            setShowGeminiInput(false);
        } catch (err: any) {
            setGeminiKeyError(err.message || 'Failed to save key.');
        } finally {
            setIsSavingGeminiKey(false);
        }
    };

    const handleRevealKey = async () => {
        if (!user.encryptedGeminiKey) return;
        setIsRevealingKey(true);
        setGeminiKeyError('');
        try {
            const plain = await revealGeminiKey(user.encryptedGeminiKey);
            setRevealedKey(plain);
            if (revealTimerRef.current) clearTimeout(revealTimerRef.current);
            revealTimerRef.current = setTimeout(() => setRevealedKey(''), REVEAL_TIMEOUT_MS);
        } catch (err: any) {
            setGeminiKeyError(err.message || 'Failed to reveal key.');
        } finally {
            setIsRevealingKey(false);
        }
    };

    const handleRemoveGeminiKey = async () => {
        setIsSavingGeminiKey(true);
        setGeminiKeyError('');
        try {
            await onSave({ encryptedGeminiKey: '' });
            setRevealedKey('');
        } catch (err: any) {
            setGeminiKeyError(err.message || 'Failed to remove key.');
        } finally {
            setIsSavingGeminiKey(false);
        }
    };

    if (!isOpen) return null;

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setIsLoading(true);
        setError('');
        try {
            saveFontSizePreferences(fontSizeMode, fontSizeMultiplier);
            
            await onSave({
                language,
                weekStartDay,
                theme,
                userDietTypes,
                knownAllergies,
                intermittentFastingEnabled,
                allowHouseholdInvitations,
            });
            onClose();
        } catch (error: any) {
            console.error("Error saving settings", error);
            setError(error.message || "Error saving details");
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4" onClick={onClose}>
            <div className="relative bg-white dark:bg-slate-900 rounded-xl shadow-2xl w-full max-w-md max-h-[min(90vh,720px)] flex flex-col border dark:border-slate-800 overflow-hidden" onClick={e => e.stopPropagation()}>
                {mcpUrlCopied && (
                    <div
                        role="status"
                        aria-live="polite"
                        className="absolute bottom-24 left-1/2 -translate-x-1/2 z-20 px-5 py-3 bg-slate-900 dark:bg-emerald-500 text-white rounded-2xl shadow-xl text-sm font-bold flex items-center gap-2 animate-fade-in pointer-events-none whitespace-nowrap"
                    >
                        <svg className="w-5 h-5 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden>
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M5 13l4 4L19 7" />
                        </svg>
                        {t('copied')}
                    </div>
                )}
                <div className="p-6 border-b border-slate-100 dark:border-slate-800 flex justify-between items-center bg-slate-50 dark:bg-slate-900 rounded-t-xl flex-shrink-0">
                    <div className="flex items-center gap-2 text-slate-800 dark:text-slate-100">
                        <CogIcon className="h-6 w-6" />
                        <h2 className="text-xl font-bold">{t('settings')}</h2>
                    </div>
                    <button onClick={onClose} className="text-slate-500 dark:text-slate-400 hover:text-slate-800 dark:hover:text-slate-200 text-2xl">&times;</button>
                </div>

                <form onSubmit={handleSubmit} className="flex flex-col flex-1 min-h-0">
                <div className="flex-1 overflow-y-auto overscroll-contain p-6 space-y-6">
                    {error && (
                        <div className="bg-red-50 dark:bg-red-900/20 text-red-800 dark:text-red-300 text-sm p-3 rounded-lg border border-red-100 dark:border-red-900/30 font-bold">
                            {error}
                        </div>
                    )}

                    <div className="space-y-6">
                        {/* Theme Selection */}
                        <div>
                            <label className="block text-sm font-bold text-slate-700 dark:text-slate-300 mb-2 uppercase tracking-wide">{t('theme')}</label>
                            <div className="flex bg-slate-100 dark:bg-slate-800 p-1 rounded-xl">
                                <button
                                    type="button"
                                    onClick={() => setTheme('light')}
                                    className={`flex-1 py-2 rounded-lg text-xs font-bold transition-all ${theme === 'light' ? 'bg-white text-slate-900 shadow-sm dark:bg-slate-700 dark:text-white' : 'text-slate-500 dark:text-slate-400 hover:text-slate-700 dark:hover:text-slate-200'}`}
                                >
                                    {t('themeLight')}
                                </button>
                                <button
                                    type="button"
                                    onClick={() => setTheme('dark')}
                                    className={`flex-1 py-2 rounded-lg text-xs font-bold transition-all ${theme === 'dark' ? 'bg-white text-slate-900 shadow-sm dark:bg-slate-700 dark:text-white' : 'text-slate-500 dark:text-slate-400 hover:text-slate-700 dark:hover:text-slate-200'}`}
                                >
                                    {t('themeDark')}
                                </button>
                                <button
                                    type="button"
                                    onClick={() => setTheme('system')}
                                    className={`flex-1 py-2 rounded-lg text-xs font-bold transition-all ${theme === 'system' ? 'bg-white text-slate-900 shadow-sm dark:bg-slate-700 dark:text-white' : 'text-slate-500 dark:text-slate-400 hover:text-slate-700 dark:hover:text-slate-200'}`}
                                >
                                    {t('themeSystem')}
                                </button>
                            </div>
                        </div>

                        <div>
                            <label className="block text-sm font-bold text-slate-700 dark:text-slate-300 mb-1.5 uppercase tracking-wide">{t('language')}</label>
                            <select 
                                value={language}
                                onChange={(e) => setLanguage(e.target.value as Language)}
                                className="w-full p-2.5 border border-slate-300 dark:border-slate-700 rounded-xl focus:ring-2 focus:ring-sky-500 bg-slate-50 dark:bg-slate-800 dark:text-white outline-none font-medium"
                            >
                                <option value="en">English</option>
                                <option value="he">עברית</option>
                                <option value="ar">العربية</option>
                                <option value="fr">Français</option>
                                <option value="de">Deutsch</option>
                                <option value="es">Español</option>
                            </select>
                        </div>
                        
                        <div>
                            <label className="block text-sm font-bold text-slate-700 dark:text-slate-300 mb-1.5 uppercase tracking-wide">{t('weekStart')}</label>
                            <select 
                                value={weekStartDay}
                                onChange={(e) => setWeekStartDay(e.target.value as WeekStartDay)}
                                className="w-full p-2.5 border border-slate-300 dark:border-slate-700 rounded-xl focus:ring-2 focus:ring-sky-500 bg-slate-50 dark:bg-slate-800 dark:text-white outline-none font-medium"
                            >
                                <option value="Monday">{t('startMonday')}</option>
                                <option value="Sunday">{t('startSunday')}</option>
                            </select>
                        </div>

                        {/* Diet Types */}
                        <div className="rounded-xl border border-slate-100 dark:border-slate-800 overflow-hidden">
                            <button
                                type="button"
                                onClick={() => setIsDietSectionExpanded((prev) => !prev)}
                                className="w-full flex items-center justify-between gap-3 p-4 bg-slate-50/80 dark:bg-slate-800/40 hover:bg-slate-100/80 dark:hover:bg-slate-800/60 transition-colors text-start"
                                aria-expanded={isDietSectionExpanded}
                            >
                                <div className="min-w-0">
                                    <p className="text-sm font-bold text-slate-700 dark:text-slate-300 uppercase tracking-wide">
                                        {t('dietLabels')}
                                    </p>
                                    {!isDietSectionExpanded && (
                                        <p className="text-xs text-slate-500 dark:text-slate-400 mt-1">
                                            {selectedDietCount > 0
                                                ? `${selectedDietCount} ${t('selected')}`
                                                : t('nothingSelected')}
                                        </p>
                                    )}
                                </div>
                                <ChevronDownIcon
                                    className={`w-5 h-5 text-slate-400 shrink-0 transition-transform duration-200 ${isDietSectionExpanded ? 'rotate-180' : ''}`}
                                />
                            </button>
                            {isDietSectionExpanded && (
                                <div className="px-4 pb-4 pt-3 border-t border-slate-100 dark:border-slate-800">
                                    <DietTypesSelector
                                        value={userDietTypes}
                                        onChange={setUserDietTypes}
                                        language={language}
                                    />
                                </div>
                            )}
                        </div>

                        {/* Known Allergies */}
                        <div className="rounded-xl border border-slate-100 dark:border-slate-800 overflow-hidden">
                            <button
                                type="button"
                                onClick={() => setIsAllergiesSectionExpanded((prev) => !prev)}
                                className="w-full flex items-center justify-between gap-3 p-4 bg-slate-50/80 dark:bg-slate-800/40 hover:bg-slate-100/80 dark:hover:bg-slate-800/60 transition-colors text-start"
                                aria-expanded={isAllergiesSectionExpanded}
                            >
                                <div className="min-w-0">
                                    <p className="text-sm font-bold text-slate-700 dark:text-slate-300 uppercase tracking-wide">
                                        {t('knownAllergies')}
                                    </p>
                                    {!isAllergiesSectionExpanded && (
                                        <p className="text-xs text-slate-500 dark:text-slate-400 mt-1">
                                            {selectedAllergyCount > 0
                                                ? `${selectedAllergyCount} ${t('selected')}`
                                                : t('nothingSelected')}
                                        </p>
                                    )}
                                </div>
                                <ChevronDownIcon
                                    className={`w-5 h-5 text-slate-400 shrink-0 transition-transform duration-200 ${isAllergiesSectionExpanded ? 'rotate-180' : ''}`}
                                />
                            </button>
                            {isAllergiesSectionExpanded && (
                                <div className="px-4 pb-4 pt-3 border-t border-slate-100 dark:border-slate-800">
                                    <KnownAllergiesSelector
                                        value={knownAllergies}
                                        onChange={setKnownAllergies}
                                        language={language}
                                    />
                                </div>
                            )}
                        </div>

                        {/* Intermittent Fasting Toggle */}
                        <div className="flex items-center justify-between gap-4 p-4 rounded-xl border border-teal-100 dark:border-teal-900/50 bg-teal-50/40 dark:bg-teal-950/20">
                            <div className="min-w-0">
                                <p className="text-sm font-bold text-slate-800 dark:text-slate-100">
                                    {t('intermittentFastingSetting')}
                                </p>
                                <p className="text-xs text-slate-500 dark:text-slate-400 mt-1">
                                    {t('intermittentFastingSettingDesc')}
                                </p>
                            </div>
                            <button
                                type="button"
                                role="switch"
                                aria-checked={intermittentFastingEnabled}
                                onClick={() => setIntermittentFastingEnabled((prev) => !prev)}
                                className={`relative inline-flex h-7 w-12 shrink-0 rounded-full transition-colors ${
                                    intermittentFastingEnabled
                                        ? 'bg-teal-600 dark:bg-teal-500'
                                        : 'bg-slate-300 dark:bg-slate-700'
                                }`}
                            >
                                <span
                                    className={`inline-block h-5 w-5 transform rounded-full bg-white shadow transition-transform mt-1 ${
                                        intermittentFastingEnabled ? 'ltr:translate-x-6 rtl:-translate-x-6' : 'ltr:translate-x-1 ltr:translate-x-1'
                                    }`}
                                />
                            </button>
                        </div>

                        {/* Allow Household Invitations Toggle */}
                        <div className="flex items-center justify-between gap-4 p-4 rounded-xl border border-sky-100 dark:border-sky-900/50 bg-sky-50/40 dark:bg-sky-950/20">
                            <div className="min-w-0">
                                <p className="text-sm font-bold text-slate-800 dark:text-slate-100">
                                    {t('allowHouseholdInvitationsSetting')}
                                </p>
                                <p className="text-xs text-slate-500 dark:text-slate-400 mt-1">
                                    {t('allowHouseholdInvitationsSettingDesc')}
                                </p>
                            </div>
                            <button
                                type="button"
                                role="switch"
                                aria-checked={allowHouseholdInvitations}
                                onClick={() => setAllowHouseholdInvitations((prev) => !prev)}
                                className={`relative inline-flex h-7 w-12 shrink-0 rounded-full transition-colors ${
                                    allowHouseholdInvitations
                                        ? 'bg-sky-600 dark:bg-sky-500'
                                        : 'bg-slate-300 dark:bg-slate-700'
                                }`}
                            >
                                <span
                                    className={`inline-block h-5 w-5 transform rounded-full bg-white shadow transition-transform mt-1 ${
                                        allowHouseholdInvitations ? 'ltr:translate-x-6 rtl:-translate-x-6' : 'ltr:translate-x-1 ltr:translate-x-1'
                                    }`}
                                />
                            </button>
                        </div>

                        <div className="pt-2">
                            <label className="block text-sm font-bold text-slate-700 dark:text-slate-300 mb-2 uppercase tracking-wide">{t('fontSize')}</label>
                            <div className="flex bg-slate-100 dark:bg-slate-800 p-1 rounded-xl mb-3">
                                <button
                                    type="button"
                                    onClick={() => {
                                        setFontSizeMode('system');
                                        applyFontSize('system');
                                    }}
                                    className={`flex-1 py-2 rounded-lg text-xs font-bold transition-all ${fontSizeMode === 'system' ? 'bg-white text-slate-900 shadow-sm dark:bg-slate-700 dark:text-white' : 'text-slate-500 dark:text-slate-400 hover:text-slate-700 dark:hover:text-slate-200'}`}
                                >
                                    {t('fontSizeSystem')}
                                </button>
                                <button
                                    type="button"
                                    onClick={() => {
                                        setFontSizeMode('custom');
                                        applyFontSize('custom', fontSizeMultiplier);
                                    }}
                                    className={`flex-1 py-2 rounded-lg text-xs font-bold transition-all ${fontSizeMode === 'custom' ? 'bg-white text-slate-900 shadow-sm dark:bg-slate-700 dark:text-white' : 'text-slate-500 dark:text-slate-400 hover:text-slate-700 dark:hover:text-slate-200'}`}
                                >
                                    {t('fontSizeCustom')}
                                </button>
                            </div>
                            {fontSizeMode === 'custom' && (
                                <>
                                    <div className="flex justify-between items-center mb-2">
                                        <span className="text-xs font-bold text-slate-500 dark:text-slate-400 uppercase tracking-wide">{t('fontSizeCustom')}</span>
                                        <span className="text-xs font-black text-sky-600 dark:text-sky-400 bg-sky-50 dark:bg-sky-900/30 px-2 py-0.5 rounded-full">{fontSizeMultiplier}%</span>
                                    </div>
                                    <input 
                                        type="range" 
                                        min="80" 
                                        max="125" 
                                        step="5"
                                        value={fontSizeMultiplier} 
                                        onChange={(e) => {
                                            const val = Math.min(125, Math.max(80, parseInt(e.target.value, 10)));
                                            setFontSizeMultiplier(val);
                                            applyFontSize('custom', val);
                                        }} 
                                        className="w-full h-2 bg-slate-200 dark:bg-slate-700 rounded-lg appearance-none cursor-pointer accent-brand-orange"
                                    />
                                    <div className="flex justify-between text-[10px] text-slate-400 dark:text-slate-500 font-bold uppercase mt-1 px-1">
                                        <span>{t('fontSmall')}</span>
                                        <span>{t('fontDefault')}</span>
                                        <span>{t('fontLarge')}</span>
                                    </div>
                                </>
                            )}
                        </div>

                        {!guidedMode && <InstallAppSetting language={language} />}

                        {/* Personal Gemini API Key */}
                        {!guidedMode && (
                        <>
                        <div className="pt-4 border-t border-slate-100 dark:border-slate-800">
                            <div className="flex items-center justify-between gap-2">
                                <button
                                    type="button"
                                    onClick={() => setIsAiSectionExpanded(prev => !prev)}
                                    className="flex-1 flex items-center gap-2 text-start min-w-0"
                                    aria-expanded={isAiSectionExpanded}
                                >
                                    <h3 className="text-sm font-black text-slate-800 dark:text-slate-100 uppercase tracking-widest flex items-center gap-2">
                                        <span>✨</span>
                                        {t('personalGeminiKey')}
                                    </h3>
                                </button>
                                <div className="flex items-center gap-2 shrink-0">
                                    <button
                                        type="button"
                                        onClick={(e) => { e.stopPropagation(); setShowGeminiInfo(prev => !prev); }}
                                        className={`w-6 h-6 rounded-full border flex items-center justify-center text-xs font-black transition-all
                                            ${showGeminiInfo
                                                ? 'bg-sky-100 dark:bg-sky-900/30 border-sky-300 dark:border-sky-700 text-sky-600 dark:text-sky-400'
                                                : 'bg-slate-100 dark:bg-slate-800 border-slate-200 dark:border-slate-700 text-slate-400 dark:text-slate-500 hover:border-slate-300 dark:hover:border-slate-600'}`}
                                        title={t('info')}
                                    >
                                        i
                                    </button>
                                    <button
                                        type="button"
                                        onClick={() => setIsAiSectionExpanded(prev => !prev)}
                                        className="p-1"
                                        aria-expanded={isAiSectionExpanded}
                                    >
                                        <ChevronDownIcon className={`w-5 h-5 text-slate-400 transition-transform duration-200 ${isAiSectionExpanded ? 'rotate-180' : ''}`} />
                                    </button>
                                </div>
                            </div>

                            {showGeminiInfo && (
                                <div className="mt-2 mb-1 bg-sky-50 dark:bg-sky-900/20 border border-sky-100 dark:border-sky-800/40 rounded-xl p-3 space-y-1.5">
                                    <p className="text-[10px] text-slate-500 dark:text-slate-400 font-medium leading-snug">
                                        {t('personalGeminiKeyDesc')}
                                    </p>
                                    <p className="text-[10px] text-sky-600 dark:text-sky-400 font-bold leading-snug flex items-start gap-1.5">
                                        <span className="shrink-0 mt-0.5">🔒</span>
                                        {t('personalGeminiKeyPrivacy')}
                                    </p>
                                </div>
                            )}

                            {isAiSectionExpanded && (
                            <>

                            {geminiKeyError && (
                                <p className="text-xs text-red-600 dark:text-red-400 font-bold mb-3 bg-red-50 dark:bg-red-900/20 px-3 py-2 rounded-xl border border-red-100 dark:border-red-900/30">
                                    {geminiKeyError}
                                </p>
                            )}

                            {hasGeminiKey && !showGeminiInput ? (
                                <div className="bg-slate-50 dark:bg-slate-800/50 rounded-2xl border border-slate-200 dark:border-slate-700 p-3 space-y-2">
                                    <div className="flex items-center justify-between gap-2">
                                        <code className="flex-grow bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 px-3 py-2 rounded-xl text-xs font-mono text-slate-500 dark:text-slate-400 truncate select-all">
                                            {revealedKey || '●●●●●●●●●●●●●●●●●●●●●●●●'}
                                        </code>
                                        <button
                                            type="button"
                                            onClick={revealedKey ? () => setRevealedKey('') : handleRevealKey}
                                            disabled={isRevealingKey}
                                            className="shrink-0 px-3 py-2 bg-sky-50 dark:bg-sky-900/20 text-sky-600 dark:text-sky-400 border border-sky-200 dark:border-sky-800/50 rounded-xl text-[10px] font-black uppercase tracking-wider hover:bg-sky-100 dark:hover:bg-sky-900/40 transition-all disabled:opacity-50"
                                        >
                                            {isRevealingKey ? '...' : revealedKey ? t('hide') : t('viewKey')}
                                        </button>
                                    </div>
                                    {revealedKey && (
                                        <p className="text-[9px] text-amber-500 dark:text-amber-400 font-bold text-center">
                                            {t('keyHidesIn30s')}
                                        </p>
                                    )}
                                    <div className="flex gap-2 pt-1">
                                        <button
                                            type="button"
                                            onClick={() => { setShowGeminiInput(true); setRevealedKey(''); }}
                                            className="flex-1 py-1.5 bg-slate-100 dark:bg-slate-700 text-slate-600 dark:text-slate-300 rounded-xl text-[10px] font-black uppercase tracking-wider hover:bg-slate-200 dark:hover:bg-slate-600 transition-all"
                                        >
                                            {t('updateKey')}
                                        </button>
                                        <button
                                            type="button"
                                            onClick={handleRemoveGeminiKey}
                                            disabled={isSavingGeminiKey}
                                            className="flex-1 py-1.5 bg-red-50 dark:bg-red-900/20 text-red-500 dark:text-red-400 border border-red-200 dark:border-red-900/30 rounded-xl text-[10px] font-black uppercase tracking-wider hover:bg-red-100 dark:hover:bg-red-900/40 transition-all disabled:opacity-50"
                                        >
                                            {isSavingGeminiKey ? '...' : t('removeKey')}
                                        </button>
                                    </div>
                                </div>
                            ) : (
                                <div className="space-y-2">
                                    <input
                                        type="password"
                                        value={geminiKeyInput}
                                        onChange={(e) => setGeminiKeyInput(e.target.value)}
                                        placeholder="AIza..."
                                        className="w-full p-2.5 border border-slate-300 dark:border-slate-700 rounded-xl focus:ring-2 focus:ring-sky-500 bg-slate-50 dark:bg-slate-800 dark:text-white outline-none font-mono text-sm"
                                    />
                                    <div className="flex gap-2">
                                        {hasGeminiKey && (
                                            <button
                                                type="button"
                                                onClick={() => { setShowGeminiInput(false); setGeminiKeyInput(''); }}
                                                className="flex-1 py-2 bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-400 rounded-xl text-xs font-bold hover:bg-slate-200 dark:hover:bg-slate-700 transition-all"
                                            >
                                                {t('cancel')}
                                            </button>
                                        )}
                                        <button
                                            type="button"
                                            onClick={handleSaveGeminiKey}
                                            disabled={!geminiKeyInput.trim() || isSavingGeminiKey}
                                            className="flex-1 py-2 bg-brand-orange text-white rounded-xl text-xs font-black uppercase tracking-wider shadow-md shadow-brand-orange/20 hover:opacity-90 disabled:opacity-50 transition-all"
                                        >
                                            {isSavingGeminiKey ? '...' : t('saveKey')}
                                        </button>
                                    </div>
                                </div>
                            )}
                            </>
                            )}
                        </div>

                        {user.apiKey?.trim() && (
                        <div className="pt-4 border-t border-slate-100 dark:border-slate-800">
                            <button
                                type="button"
                                onClick={() => setIsIntegrationsExpanded(prev => !prev)}
                                className="w-full flex items-center justify-between gap-2 text-start"
                                aria-expanded={isIntegrationsExpanded}
                            >
                                <h3 className="text-sm font-black text-slate-800 dark:text-slate-100 uppercase tracking-widest flex items-center gap-2">
                                    <ShareIcon className="w-4 h-4 text-brand-orange" />
                                    {t('integrations')}
                                </h3>
                                <ChevronDownIcon className={`w-5 h-5 text-slate-400 shrink-0 transition-transform duration-200 ${isIntegrationsExpanded ? 'rotate-180' : ''}`} />
                            </button>

                            {isIntegrationsExpanded && (
                            <div className="space-y-3 mt-3">
                                <div className="bg-slate-50 dark:bg-slate-800/50 p-4 rounded-2xl border border-slate-100 dark:border-slate-800">
                                    <div className="flex justify-between items-center mb-1">
                                        <span className="text-[10px] font-black text-slate-400 dark:text-slate-500 uppercase tracking-widest">{t('toolsApi')}</span>
                                        <span className="px-1.5 py-0.5 bg-sky-500/10 text-sky-500 text-[9px] font-black uppercase rounded tracking-tighter border border-sky-500/20">{t('aiEnabled')}</span>
                                    </div>
                                    <p className="text-[10px] text-slate-500 dark:text-slate-400 mb-3 leading-tight">{t('toolsApiDescription')}</p>
                                    <div className="space-y-2">
                                        <div className="flex flex-col gap-1">
                                            <span className="text-[9px] font-bold text-slate-400 dark:text-slate-600 uppercase ml-1">{t('toolsApiBaseUrl')}</span>
                                            <div className="flex gap-2">
                                                <code className="flex-grow bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 px-3 py-1.5 rounded-lg text-[10px] font-mono text-slate-600 dark:text-slate-300 break-all">
                                                    {toolsApiBaseUrl}
                                                </code>
                                                <button
                                                    type="button"
                                                    onClick={handleCopyToolsApiUrl}
                                                    className={`p-1.5 rounded-lg transition-all shrink-0 ${
                                                        toolsApiUrlCopied
                                                            ? 'bg-emerald-500 text-white'
                                                            : 'bg-slate-800 text-white dark:bg-slate-200 dark:text-slate-900 hover:opacity-90'
                                                    }`}
                                                    title={toolsApiUrlCopied ? t('copied') : t('toolsApiCopyUrl')}
                                                    aria-label={toolsApiUrlCopied ? t('copied') : t('toolsApiCopyUrl')}
                                                >
                                                    {toolsApiUrlCopied ? (
                                                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden>
                                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M5 13l4 4L19 7" />
                                                        </svg>
                                                    ) : (
                                                        <ShareIcon className="w-4 h-4" />
                                                    )}
                                                </button>
                                            </div>
                                        </div>
                                        <p className="text-[9px] text-slate-400 dark:text-slate-500 font-medium leading-snug">
                                            {t('toolsApiAuthNote')}
                                        </p>
                                    </div>
                                </div>

                                <div className="bg-slate-50 dark:bg-slate-800/50 p-4 rounded-2xl border border-slate-100 dark:border-slate-800">
                                    <div className="flex justify-between items-center mb-1">
                                        <span className="text-[10px] font-black text-slate-400 dark:text-slate-500 uppercase tracking-widest">{t('mcpServer')}</span>
                                        <span className="px-1.5 py-0.5 bg-emerald-500/10 text-emerald-500 text-[9px] font-black uppercase rounded tracking-tighter border border-emerald-500/20">{t('mcpConnected')}</span>
                                    </div>
                                    <p className="text-[10px] text-slate-500 dark:text-slate-400 mb-3 leading-tight">{t('mcpDescription')}</p>
                                    
                                    <div className="space-y-2">
                                        <div className="flex flex-col gap-1">
                                            <span className="text-[9px] font-bold text-slate-400 dark:text-slate-600 uppercase ml-1">{t('mcpUrl')}</span>
                                            <div className="flex gap-2">
                                                <code className="flex-grow bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 px-3 py-1.5 rounded-lg text-[10px] font-mono text-slate-600 dark:text-slate-300 break-all">
                                                    {`${MCP_SSE_BASE}?${user.apiKey}`}
                                                </code>
                                                <button 
                                                    type="button"
                                                    onClick={handleCopyMcpUrl}
                                                    className={`p-1.5 rounded-lg transition-all shrink-0 ${
                                                        mcpUrlCopied
                                                            ? 'bg-emerald-500 text-white'
                                                            : 'bg-slate-800 text-white dark:bg-slate-200 dark:text-slate-900 hover:opacity-90'
                                                    }`}
                                                    title={mcpUrlCopied ? t('copied') : t('mcpCopyUrl')}
                                                    aria-label={mcpUrlCopied ? t('copied') : t('mcpCopyUrl')}
                                                >
                                                    {mcpUrlCopied ? (
                                                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden>
                                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M5 13l4 4L19 7" />
                                                        </svg>
                                                    ) : (
                                                        <ShareIcon className="w-4 h-4" />
                                                    )}
                                                </button>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                            )}
                        </div>
                        )}
                        </>
                        )}
                    </div>
                </div>

                    <div className="flex justify-end p-6 pt-4 border-t border-slate-100 dark:border-slate-800 gap-3 flex-shrink-0 bg-white dark:bg-slate-900">
                        <button 
                            type="button" 
                            onClick={onClose} 
                            className="px-6 py-2.5 bg-slate-100 dark:bg-slate-800 text-slate-700 dark:text-slate-300 rounded-xl font-bold hover:bg-slate-200 dark:hover:bg-slate-700 transition-all"
                        >
                            {t('cancel')}
                        </button>
                        <button 
                            type="submit" 
                            disabled={isLoading}
                            className="px-8 py-2.5 bg-brand-sage text-white rounded-xl font-black shadow-lg shadow-brand-sage/20 hover:opacity-90 disabled:opacity-50 transition-all uppercase tracking-widest text-sm"
                        >
                            {isLoading ? '...' : t('save')}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
};

export default SettingsModal;
